import { FC, useEffect, useState } from "react";
import { ClickAwayListener, Collapse, ListItemButton, MenuItem, MenuList, Popover } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 as uuid } from "uuid";
import { AppMenuSection } from "../../../../../../../../common/routesUtils";
import { PageType } from "../../../../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../../../../context/BreadcrumbsProvider";
import SectionItem from "../SectionItem";

interface Props {
	menuIsOpen: boolean;
	item: AppMenuSection;
}

const MultipleRoutes: FC<Props> = ({ menuIsOpen, item }) => {
	const navigateTo = useNavigate();
	const { t } = useTranslation("navigation");
	const { breadcrumbs, visitPage } = useBreadcrumbs();
	const [showRoutes, setShowRoutes] = useState<boolean>(false); // expand/collapse state
	const [anchor, setAnchor] = useState<HTMLDivElement | null>(null); // popover anchor

	const id = showRoutes ? uuid() : undefined; // unique popover id

	// determine if parent or any child is selected based on last breadcrumb
	const lastPageType = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].pageType : null;
	const isSelected = lastPageType === item.pageType || item.items.some((i) => i.pageType === lastPageType);

	// toggle routes menu open/close
	const handleRoutesMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (menuIsOpen) {
			setShowRoutes(!showRoutes);
		} else {
			setShowRoutes(true);
			setAnchor(e.currentTarget); // set anchor for popover when menu is closed
		}
	};

	// close popover/menu
	const closeMenu = () => {
		setShowRoutes(false);
		setAnchor(null);
	};

	// handle click on a child route
	const itemClicked = (route: string, pageType: string) => {
		if (!menuIsOpen) closeMenu(); // close popover if menu collapsed
		visitPage(pageType as PageType, true); // update breadcrumb (from menu)
		navigateTo(route); // navigate to route
	};

	// update menu open/collapse state when menu toggles or selection changes
	useEffect(() => {
		setAnchor(null);
		if (!menuIsOpen) {
			closeMenu();
		} else {
			setShowRoutes(isSelected);
		}
	}, [menuIsOpen, isSelected]);

	return (
		<>
			{/* main section item */}
			<SectionItem
				expand={showRoutes ? "less" : "more"} // toggle icon
				itemKey={item.key}
				isSelected={isSelected}
				menuIsOpen={menuIsOpen}
				onClick={handleRoutesMenu} // open/collapse
			/>

			{/* expanded routes when menu is open */}
			{menuIsOpen && (
				<Collapse in={showRoutes} timeout="auto" unmountOnExit>
					{item.items.map((i) => {
						const childSelected = lastPageType === i.pageType;
						return (
							<ListItemButton
								selected={childSelected}
								onClick={() => itemClicked(i.route, i.pageType)}
								key={i.route}
								sx={{ paddingInlineStart: 8 }} // indent children
							>
								{t([`breadcrumbs/${i.key}`, `breadcrumbs/<${i.key}>`])}
							</ListItemButton>
						);
					})}
				</Collapse>
			)}

			{/* popover when menu is collapsed */}
			{!menuIsOpen && (
				<Popover
					id={id}
					open={showRoutes}
					anchorEl={anchor}
					anchorOrigin={{ vertical: "center", horizontal: "right" }}
				>
					<ClickAwayListener onClickAway={closeMenu}>
						<MenuList autoFocusItem>
							{item.items.map((i) => {
								const childSelected = lastPageType === i.pageType;
								return (
									<MenuItem
										selected={childSelected}
										key={i.route}
										onClick={() => itemClicked(i.route, i.pageType)}
									>
										{t([`breadcrumbs/${i.key}`, `breadcrumbs/<${i.key}>`])}
									</MenuItem>
								);
							})}
						</MenuList>
					</ClickAwayListener>
				</Popover>
			)}
		</>
	);
};

export default MultipleRoutes;
